import { inject, Injectable } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { TranslateService, TranslateStore } from '@ngx-translate/core';
import { getLocaleForLang, SUPPORTED_LANGS } from '@constants';
import { firstValueFrom, tap } from 'rxjs';
import { patchTranslateServiceCompat } from '@shared/i18n/translate-service-compat';
import 'moment/locale/en-gb';
import 'moment/locale/pl';
import 'moment/locale/ru';
import 'moment/locale/uk';

@Injectable({
  providedIn: 'root',
})
export class AppInitService {
  private readonly translateService = inject(TranslateService);
  private readonly translateStore = inject(TranslateStore);
  private readonly dateAdapter = inject<DateAdapter<unknown>>(DateAdapter);

  public async ensureLangsLoaded(): Promise<boolean> {
    patchTranslateServiceCompat(this.translateService, this.translateStore);
    this.translateService.addLangs(SUPPORTED_LANGS);

    await this.waitForLangsLoaded(SUPPORTED_LANGS);
    await firstValueFrom(this.translateService.setFallbackLang('pl'));
    await firstValueFrom(this.translateService.use('pl'));

    // WCAG 2.1
    this.updateLocale(this.translateService.getCurrentLang() || this.translateService.getFallbackLang());

    this.translateService.onLangChange.subscribe(({ lang }) => {
      this.updateLocale(lang);
    });

    return true;
  }

  private async waitForLangsLoaded(langsArray: string[]): Promise<boolean> {
    await Promise.all(
      langsArray.map(lang =>
        firstValueFrom(
          this.translateService.currentLoader
            .getTranslation(lang)
            .pipe(tap(translations => this.translateService.setTranslation(lang, translations, true)))
        )
      )
    );

    // eslint-disable-next-line no-console
    console.info('All languages loaded');

    return true;
  }

  private updateLocale(lang: string | null | undefined): void {
    if (!lang) {
      return;
    }

    document.documentElement.lang = lang;
    this.dateAdapter.setLocale(getLocaleForLang(lang));
  }
}
